import {
    Subscription,
    takeUntil,
    Subject, switchMap,
    debounceTime, distinctUntilChanged, map,
    startWith,
    merge,
    fromEvent
} from "rxjs";

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    booleanAttribute,
    HostListener,
    inject,
    TemplateRef
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    FormControl, ControlValueAccessor, NgControl, Validators,
    FormsModule, ReactiveFormsModule,
    ControlContainer,
    NgForm,
} from "@angular/forms";
import {
    MatAutocomplete,
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
    MatOptgroup
} from "@angular/material/autocomplete";
import { MatFormFieldControl } from "@angular/material/form-field";
import { FormUtil } from "../../form-util";
import { IAutocompleteController } from "../../../interfaces/autocomplete-controller";
import { Observable } from "rxjs";
import { IAutocompleteResults } from "../../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../../interfaces/autocomplete-option";
import { AsyncPipe } from "@angular/common";
import { MatInput } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import {
    MatChipInputEvent, MatChipsModule
} from "@angular/material/chips";
import {
    ArrayUtil,
    StringUtil
} from "@shopliftr/common-js/data";
import { NgTemplateOutlet } from "@angular/common";
import { RxjsUtil } from "../../../utils/rxjs-util";
import { COMMON_APP_SERVICE } from "../../../application/constants/app-tokens";
import { EnhancedControlDirective } from "../../directives/form-control/enhanced-control.directive";
import { PanelTriggerHostDirective } from "../../../directives/panel-trigger/panel-trigger-host.directive";

/**
 * GenericAutocomplete Form Control Component
 * Implement IAutocompleteController with to configure the Generic Autocomplete Form Control
 *
 * This follows the custom form control pattern
 */
@Component({
    selector: "mgd-generic-autocomplete",
    templateUrl: "./generic-autocomplete-mat-control.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatAutocomplete,
        MatAutocompleteModule,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
        MatInput,
        MatIconModule,
        MatChipsModule,
        MatOptgroup,
        NgTemplateOutlet,
        EnhancedControlDirective
    ],
    providers: [{
        provide: MatFormFieldControl,
        useExisting: GenericAutocompleteMatControlComponent
    },
    {
        provide: ControlContainer,
        useExisting: NgForm
    }],
    hostDirectives: [{
        directive: EnhancedControlDirective,
        inputs: ["focusHotkey"]
    }]
})
export class GenericAutocompleteMatControlComponent<T = any>
implements ControlValueAccessor, MatFormFieldControl<T | Array<T>>, AfterViewInit, OnChanges, OnDestroy, OnInit {

    /**
     * nextId used in generating id for this component
     *
     * @static
     * @memberof GenericAutocompleteMatControlComponent
     */
    static nextId = 0;

    /**
     * MatFormFieldControl property id
     * Returns the ID of an element in the component's template for <mat-form-field> to associate all of its labels
     * and hints with. Use nextId to generate a unique string when created.
     * Used in setDescribedByIds to set element id that will be used for the aria-describedby attribute of this control.
     * @memberof GenericAutocompleteMatControlComponent
     */
    @HostBinding() id = `generic-autocomplete-${GenericAutocompleteMatControlComponent.nextId++}`;

    /**
     * Sets tabindex="-1" on the host element so it is programmatically focusable.
     * Custom component host elements are not focusable by default because they lack a tabindex.
     * Without this, calling .focus() on the host (e.g. via EnhancedControlDirective.focus())
     * is silently ignored by the browser, and the @HostListener("focus") onFocus() never fires.
     * Using -1 keeps the host out of the natural tab order — only the inner <input> should be a tab stop.
     */
    @HostBinding("attr.tabindex") tabIndex = -1;

    @HostBinding("attr.aria-describedby") describedBy = "";

    // id name for the input field in html template
    @ViewChild("genericInput", { static: true }) input: ElementRef<HTMLInputElement>;

    /**
     * Reference to the filtered options template.
     */
    @ViewChild("filteredOptions", { read: TemplateRef }) filteredOptions?: TemplateRef<unknown>;

    /**
     * Reference to the Autocomplete Options Menu
     *
     * @type {MatAutocomplete}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @ViewChild("auto") autocomplete: MatAutocomplete;

    /**
     * Emits object selected from the Mat Autocomplete dropdown, MatSelect or other elements that selects
     * an item from a list.
     *
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Output() optionSelected = new EventEmitter<T>(false);

    /**
     * MatFormFieldControl property that tells the form field when it should be in the disabled state.
     * In addition to reporting the right state to the form field, we need to set the disabled state
     * on the individual inputs that make up our component.
     *
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Input({ transform: booleanAttribute }) disabled: boolean = false;

    /**
     * MatFormFieldControl property that tells <mat-form-field> what to use as a placeholder
     *
     * @readonly
     * @type {string}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Input() placeholder: string;

    /**
     * The input used to set the required value.
     *
     * Using a aliased entry allows allows for a required setter and getter
     *
     * ngOnChanges will call the required setter when this input changes
     */
    @Input("required") requiredInput: boolean;

    /**
     * The input used to set if the form control is allowed to take any input in the input field,
     * meaning the user is not restricted from selecting from the autocomplete options,
     * they can enter their own value for the form control.
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Input("allowAnyEntry") allowAnyEntryInput: boolean;

    /**
     * Service that implements the autocomplete$ function
     *
     * @type {IAutocompleteController}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Input() controller: IAutocompleteController;

    /**
     * Optional. Pass options to the autocomplete function
     *
     * @type {*}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Input() options: any;

    /**
     * Multiple Selection
     *
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    @Input("multiple") multipleInput: boolean;

    /**
     * Reference to the `MatAutocompleteTrigger` applied to the inner `<input mgdEnhancedControl>`.
     * Used in `ngAfterViewInit` to wire the outer `PanelTriggerHostDirective`
     * with `ignoreCloseAll: true`, so consumers can read `isOpen` / call `close()`
     * against the host element without creating a duplicate `closeAllPanels$` subscription.
     */
    // eslint-disable-next-line @typescript-eslint/prefer-readonly -- @ViewChild fields assigned by Angular; cannot be readonly
    @ViewChild(MatAutocompleteTrigger) private _autoTrigger!: MatAutocompleteTrigger;

    /**
     * MatFormFieldControl property that specifies a unique string for the type of control in form field.
     * The <mat-form-field> will add a class based on this type that can be used to easily apply special
     * styles to a <mat-form-field> that contains a specific type of control.
     * The form field will add the class mat-form-field-type-example-form-control.
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    controlType = "generic-autocomplete";

    /**
     * MatFormFieldControl property indicates whether the associated NgControl is in an error state.
     * Can show an error if the input is invalid and our component has been touched.
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    errorState = false;

    /**
     * MatFormFieldControl property indicates whether or not the form field control should be considered to be in a focused state.
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    focused = false;

    /**
     * MatFormFieldControl property
     * MatFormField uses OnPush strategy, stateChanges will tell the parent that something happened
     * in the form field control that may require the form field to run change detection.
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    stateChanges = new Subject<void>();

    /**
     * Internal form component to represent the input field the where the user will enter an email address
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    inputFormControl = new FormControl();

    /**
     * Observable that represents the email addresses that are filtered by the users's input
     *
     * @type {Observable<Array<IAutocompleteOption<T>>>}
     * @memberof GenericAutocompleteMatControlComponent
     */
    filteredItems$: Observable<Array<IAutocompleteOption<T>>>;

    /**
     * will be shown in Mat-Optgroup as a heading. This is assigned in the controller
     *
     * @type {string}
     * @memberof GenericAutocompleteMatControlComponent
     */
    groupMessage: string;

    /**
     * Array that represents the internal array that user has selected
     *
     * @type {Array<T>}
     * @memberof GenericAutocompleteMatControlComponent
     */
    selectedItems: Array<T> = [];

    /**
     * Subscription for any state changes that are emitted from the ngControl
     *
     * @type {Subscription}
     * @memberof GenericAutocompleteMatControlComponent
     */
    stateChangesSubscription: Subscription;

    /**
     * The control directive for this component
     * This is required as part of the MatFormFieldControl interface
     */
    readonly ngControl = inject(NgControl, {
        optional: true,
        self: true
    });

    /**
     * Callback function registered with registerOnChange
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    onChange: (_: any) => void;

    /**
     * Callback function registered with registerOnTouched
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    onTouched: () => void;

    private _shouldLabelFloat: boolean = false;

    private _value: T | Array<T>;

    /**
     * Application service
     */
    private readonly _app = inject(COMMON_APP_SERVICE);

    /**
     * Used to mark the OnPush view dirty when `writeValue` / `setDisabledState`
     * programmatically mutate component state outside of a user interaction
     * (e.g. `formGroup.reset()`), since no host event triggers change detection in that case.
     */
    private readonly _cdRef = inject(ChangeDetectorRef);

    /**
     * User set value for the required setter and getter
     */
    private _required: boolean;

    private _multiple: boolean = false;

    private _allowAnyEntry: boolean = false;

    private readonly _clearOnBlur$ = new Subject<void>();

    /**
     * The `PanelTriggerHostDirective` composed onto the outer host element
     * via `EnhancedControlDirective → PanelTriggerHostDirective`.
     * Wired in `ngAfterViewInit` with `ignoreCloseAll: true` because the inner
     * `<input mgdEnhancedControl>` already owns the `closeAllPanels$` subscription.
     */
    private readonly _panelTrigger = inject(PanelTriggerHostDirective, { self: true });

    private readonly _destroyRef = inject(DestroyRef);

    constructor() {

        if (this.ngControl) {
            // Note: we provide the value accessor through here, instead of
            // the `providers` to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }

    }

    /**
     * MatFormFieldControl property used to indicate whether the input is required.
     * <mat-form-field> uses this information to add a required indicator to the placeholder.
     *
     * @readonly
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    get required(): boolean {
        return this._required || this.ngControl?.control?.hasValidator(Validators.required);
    }

    set required(value: boolean) {

        this._required = value;
        this.stateChanges.next();
    }

    /**
     * multiple attribute indicates if the component will accept more than one email.
     * If multiple is true, then the component will accept more than one email and
     * present each email as a chip in a chip list above the input field.
     * If multiple is set to false, then the component will only have the
     *  input field and the user can only input one email address.
     * @type {boolean}
     * @memberof EmailComponent
     */
    get multiple(): boolean {
        return this._multiple;
    }

    /**
     * Multiple attribute when set to true allows the user to enter more than one email.
     * Setting multiple to false will only allow one email.
     *
     * @memberof EmailComponent
     */
    set multiple(value: boolean) {
        this._multiple = value;
        this.stateChanges.next();
    }

    /**
     * allowAnyEntry attribute when set to true allows the user to enter anything in the textbox.
     * Setting allowAnyEntry to false will restrict the values to the items in the autocomplete options.
     * allowAnyEntry is false by default
     *
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    get allowAnyEntry(): boolean {
        return this._allowAnyEntry;
    }

    /**
     * Setter for the allowAnyEntry attribute, which determines if the user can enter any input in the text field.
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    set allowAnyEntry(value: boolean) {
        this._allowAnyEntry = value;
        this.stateChanges.next();
    }

    /**
     * Placeholder logic for the input field. It will only show the placeholder if the mat label is floating or when input field is focused.
     *
     * @readonly
     * @type {string}
     * @memberof GenericAutocompleteMatControlComponent
     */
    get inputPlaceholder(): string {
        if (this.shouldLabelFloat && this.placeholder) {
            return this.placeholder;
        }
        else {
            return "";
        }
    }

    /**
     *  MatFormFieldControl property indicates whether the form field control is empty.
     *  For our control, we'll consider it empty if all of the parts are empty.
     *
     * @readonly
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    get empty(): boolean {

        if (this._multiple) {
            return FormUtil.emptyValue(this.value) && (!this.multiple || ArrayUtil.isEmpty(this.selectedItems));
        }
        else {
            return FormUtil.emptyValue(this.value);
        }

    }

    /**
     * MatFormFieldControl property used to indicate whether the label should be in the floating position.
     * Use same logic as matInput and float the placeholder when the input is focused or non-empty.
     * Since the placeholder will be overlapping our control when when it's not floating,
     * should hide the – characters when it's not floating.
     *
     * @readonly
     * @type {boolean}
     * @memberof GenericAutocompleteMatControlComponent
     */
    get shouldLabelFloat(): boolean {

        return this.focused || !this.empty || this._shouldLabelFloat;
    }

    /**
     * Setter for MatFormFieldControl property
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    set shouldLabelFloat(value: boolean) {

        this._shouldLabelFloat = value;
    }

    /**
     * MatFormFieldControl property value
     *
     * @type {T}
     * @memberof GenericAutocompleteMatControlComponent
     */
    get value(): T | Array<T> {

        // returns the internal value of the component
        return this._value;
    }

    /**
     * Setter for MatFormFieldControl property value
     * Also, calls stateChanges.next,  if onChange is registered will call the callback functions for onChange and onTouched
     *
     * @memberof GenericAutocompleteMatControlComponent
     */
    set value(newValue: T | Array<T>) {

        if (this._multiple) {
            const newValues = new Set<T>();
            (newValue as Array<T>)?.forEach((item) => {
                newValues.add(item);

            });
            this.selectedItems = Array.from(newValues);
            this._value = this.selectedItems;

        }
        else {
            newValue = newValue || undefined;
            if (newValue) {
                this.inputFormControl.setValue(StringUtil.sanitizeString(
                    this.controller.getAutocompleteDisplayName(newValue as T, this.options)
                ));
            }
            else {
                this.inputFormControl.setValue(undefined, { emitEvent: true });
            }
            this._value = newValue;

        }

        this.stateChanges.next();

        if (this.onChange) {
            this.onChange(newValue);
        }

        this._setErrorState();
    }

    /**
     * Listener for blur event. It will set the focused state and trigger state changes
     */
    @HostListener("blur") onBlur(): void {
        this.focused = false;
        if (!this.disabled) {
            // Trigger _clearOnBlur$, which (after a short debounce to let onOptionSelected finish first)
            // either commits the typed free-text entry (multiple + allowAnyEntry), or clears the input
            // field when the value is restricted to selecting from the autocomplete options.
            this._clearOnBlur$.next();

            // NOTE: This is a user interaction, as blur is called when the user leaves the input field,
            // call OnTouched() function here
            if (this.onTouched) {
                this.onTouched();
            }

            this.stateChanges.next();
            this._setErrorState();
        }
    }

    /**
     * Host listener for the focus event on the component
     */
    @HostListener("focus") onFocus(): void {
        if (!this.disabled && !this.focused) {
            this.input.nativeElement.focus();
        }
    }

    /**
     * Called when user focuses on input field. It will set the focused state and trigger state changes
     */
    onInputFocus(): void {
        if (!this.disabled && !this.focused) {
            this.focused = true;
            if (this.value) {
                // Select the value in the input field when it has been set.
                const inputElement = this.input.nativeElement;
                inputElement.setSelectionRange(0, inputElement.value.length);
            }
            this.stateChanges.next();
        }
    }

    trackById: any = (index: number, item: any) => item && item.id ? item.id : item;

    /**
     * Angular onChanges Lifecycle event
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes.disabled) {
            this._setDisabledState(changes.disabled.currentValue as boolean);
        }

        if (changes.requiredInput) {
            this.required = changes.requiredInput.currentValue;
        }

        if (changes.placeholder) {
            this.stateChanges.next();
        }

        if (changes.allowAnyEntryInput) {
            this.allowAnyEntry = changes.allowAnyEntryInput.currentValue;
            this.stateChanges.next();
        }

        // change internal value when multipleInput changes
        if (changes.multipleInput) {
            const previousValue = this.value || undefined;
            this.multiple = changes.multipleInput.currentValue;
            if (this.multiple && previousValue) {

                if (Array.isArray(previousValue)) {
                    this.selectedItems = previousValue;
                }
                else {
                    this.selectedItems = [];
                    this.selectedItems.push(previousValue as T);
                }

                this.value = this.selectedItems;
                this.inputFormControl.setValue(undefined, { emitEvent: false });
            }
            else if (previousValue) {
                let newValue;
                if (!Array.isArray(previousValue)) {
                    newValue = previousValue;
                }

                if (this.selectedItems.length > 0) {
                    newValue = this.selectedItems[0];
                    this.selectedItems = [];
                }
                this.value = newValue;
            }
        }

        this._setErrorState();
    }

    /**
     * Angular AfterViewInit Lifecycle event.
     *
     * Wires the outer host's `PanelTriggerHostDirective` to the inner
     * `MatAutocompleteTrigger` with `ignoreCloseAll: true`. The inner
     * `<input mgdEnhancedControl [matAutocomplete]="auto">` already owns
     * the `closeAllPanels$` subscription via its own composed
     * `PanelTriggerHostDirective`. The outer trigger is kept here only so
     * consumers can read `isOpen` / call `close()` against the host element.
     */
    ngAfterViewInit(): void {
        this._panelTrigger.setTrigger(this._autoTrigger, /* ignoreCloseAll */ true);
    }

    /**
     * Angular onDestroy Lifecycle event
     */
    ngOnDestroy(): void {

        this.stateChanges.complete();

        this.stateChangesSubscription.unsubscribe();

        this._clearOnBlur$.complete();

    }

    /**
     * Angular onInit Lifecycle event
     *
     * Initialize subscription and observables
     */
    ngOnInit(): void {

        if (!this.controller) {
            const error = new Error("Controller not defined");
            this._app.errorHandler.handleComponentError(error);
            throw error;
        }

        // Setup _clearOnBlur$ Subscription, validates the input field after 150 ms user leaves the input field (onBlur event)
        this._clearOnBlur$.pipe(
            // delay 150 ms, to allow enough time for the onOptionSelected function to finish
            debounceTime(150),
            // Complete when option selected
            takeUntil(this.optionSelected),
            // resubscribe after completion until the subscription is closed
            RxjsUtil.repeatUntilClosed(this._clearOnBlur$)
        ).subscribe(() => {
            this._validateInputField();
        });

        // setup an observable on focus events and return "" to trigger a change on inputFormControl
        const focus$ = fromEvent(this.input.nativeElement, "focus").pipe(
            map(() => "")
        );

        // setup an observable for changes on the inputFormControl
        const formControlChanges$ = this.inputFormControl.valueChanges.pipe(
            startWith(this.inputFormControl.value ?? ""),
            debounceTime(100),
            distinctUntilChanged()
        );

        // merge value changes with focus events so a focus will trigger the autocomplete lookup
        this.filteredItems$ = merge(formControlChanges$, focus$).pipe(
            switchMap((value: string) => {

                return this.controller.autocomplete$(value, this.options).pipe(
                    map((results: IAutocompleteResults) => {
                        this.groupMessage = results.groupMessage ? results.groupMessage : undefined;
                        return results.options;
                    })
                );
            })
        );

        this.stateChangesSubscription = this.ngControl.statusChanges
            .pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this._setErrorState());
    }

    /**
     * ControlValueAccessor interface method used to register a callback function that will be called whenever the value
     * of the custom form control changes. The callback function is passed as an argument.
     * @param fn
     */
    registerOnChange(fn: any): void {

        this.onChange = fn;
    }

    /**
     * ControlValueAccessor interface method registers the onTouched() callback function, that will be called
     * when the custom form control is touched.
     * @param fn
     */
    registerOnTouched(fn: any): void {

        this.onTouched = fn;
    }

    /**
     * ControlValueAccessor interface method that updates the value of the custom control.
     * Marks the OnPush view for check since this is a programmatic write (e.g. `formGroup.reset()`)
     * with no host event to otherwise trigger change detection.
     * @param obj
     */
    writeValue(obj: T | Array<T>): void {

        this.value = obj;
        this._cdRef.markForCheck();
    }

    /**
     * MatFormFieldControl method will be called when the form field is clicked on.
     */
    onContainerClick(): void {

        if (!this.disabled && !this.focused) {
            this.input.nativeElement.focus();
        }
    }

    /**
     * MatFormFieldControl method used by the <mat-form-field> to set element ids that should be used for the aria-describedby attribute.
     * The ids are controlled through the form field as hints or errors are conditionally displayed and should be reflected in the control's
     * aria-describedby attribute for an improved accessibility experience.
     * The setDescribedByIds method is invoked whenever the control's state changes.
     * @param ids
     */
    setDescribedByIds(ids: Array<string>): void {

        this.describedBy = ids.join(" ");
    }

    /**
     * ControlValueAccessor Function that is called by the forms API when the control status changes to or from 'DISABLED'.
     * Depending on the status, it enables or disables the appropriate DOM element.
     * Marks the OnPush view for check since this is a programmatic write with no host event
     * to otherwise trigger change detection.
     * @param isDisabled boolean value that indicates whether the control should be disabled or not
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this._setDisabledState(isDisabled);
        this._cdRef.markForCheck();
    }

    /**
     * NOTE: This is optional and goes with the @Output optionSelected event binding.
     * This function is called when optionSelected is fired from the Material Autocomplete or Material Select control.
     * This happens when the user selects an item from the list.
     * Notifies the parent when an autocomplete option has been explicitly selected
     */
    onOptionSelected(event: MatAutocompleteSelectedEvent): void {

        if (this._multiple) {
            this._addItem(event.option.value as T);
        }
        else {
            this.value = event.option.value;
            this.inputFormControl.setValue(this.controller.getAutocompleteDisplayName(event.option.value, this.options));
        }
        // NOTE: This is a user interaction, as this is called when the user selects from MatOption List,
        // call OnTouched() function here
        if (this.onTouched) {
            this.onTouched();
        }

        this.optionSelected.emit(event.option.value as T);
    }

    /**
     * Called when the user presses Enter (or another configured chip separator key) while typing
     * in the input field. Only applicable when `multiple` and `allowAnyEntry` are both enabled —
     * commits the typed free-text value as a new selected item and adds a chip to the chip grid.
     * Mirrors the `EmailComponent.onInputTokenEnd` pattern.
     * @param event MatChipInputEvent that contains the value of the input field
     */
    public onMatChipInputTokenEnd(event: MatChipInputEvent): void {
        if (this._multiple && this._allowAnyEntry) {
            const value = StringUtil.sanitizeString(event.value as string) as T;
            this._addItem(value, event);
        }
    }

    /**
     * Removes item from the selectedItems array
     * @param item
     */
    public remove(item: T): void {
        const index = this.selectedItems.indexOf(item);

        if (index >= 0) {
            this.selectedItems.splice(index, 1);
            this.value = this.selectedItems;
        }
    }

    /**
     * Set this.errorState based on the ngControl state
     */
    private _setErrorState(): void {

        if (this.multiple && this.required) {
            // set errorState to true if this.selectedItems is empty when the component is required
            this.errorState = this.ngControl.touched && (ArrayUtil.isEmpty(this.selectedItems));
        }
        else {
            this.errorState = this.ngControl.touched && this.ngControl.invalid;
        }

    }

    /**
     * Disable or enable elements in the custom control
     */
    private _setDisabledState(isDisabled: boolean): void {

        if (isDisabled) {
            this.inputFormControl.disable();
        }
        else {
            this.inputFormControl.enable();
        }

        this.stateChanges.next();
    }

    /**
     * Adds item to the selectedItems array if not in the array yet
     * @param newItem
     * @param event
     */
    private _addItem(newItem: T, event?: MatChipInputEvent): void {
        if (this._multiple) {

            if (newItem) {
                const isUnique = this.controller.isUnique(newItem, this.selectedItems);
                if (isUnique) {
                    this.selectedItems.push(newItem);
                }

                event?.chipInput!.clear();
                this.inputFormControl.setValue(undefined);
                this.value = this.selectedItems;
            }

        }
    }

    /**
     * Validates the input field.
     *
     * - Single-value mode: clears the value unless it matches the display value of the internal
     *   value of the component, or `allowAnyEntry` is enabled.
     * - Multiple-value mode: when `allowAnyEntry` is enabled, commits any typed free-text entry as a
     *   new selected item (e.g. when the user blurs instead of pressing Enter); otherwise clears the
     *   input field since the value is restricted to selecting from the autocomplete options.
     */
    private _validateInputField(): void {

        const inputValue = this.inputFormControl.value;

        if (this._multiple) {
            if (this._allowAnyEntry && !StringUtil.emptyString(inputValue as string)) {
                // allowAnyEntry is true, commit the typed free-text value as a new selected item
                this._addItem(inputValue as T);
            }
            else {
                // value is restricted to Autocomplete options, clear the input value
                this.inputFormControl.setValue(undefined, { emitEvent: false });
            }
            return;
        }

        // if inputValue is empty, clear the value
        if (StringUtil.emptyString(inputValue as string)) {
            this.value = undefined;
        }
        else {
            // Validate that the input field matches display name of the value object
            if (!this.allowAnyEntry) {
                if (!(this.controller.getAutocompleteDisplayName(this.value as T, this.options) === inputValue)) {
                    this.value = undefined;
                }
            }
            else {
                // allowAnyEntry is true, set the internal value to the inputFormControl.value
                this.value = this.inputFormControl.value;
            }
        }
    }

}
